import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Modal, Button, ProgressBar, Form, Spinner, Alert } from "react-bootstrap";
import "./RecopiladorPage.css";

const editableFields = [
  {
    section: "General",
    fields: [
      { name: "nombre", label: "Nombre y Apellido", type: "text" },
      { name: "tipo", label: "Tipo", type: "select", optionsFrom: "tipo", disabled: true },
      { name: "celular", label: "Celular", type: "text" },
      { name: "correo", label: "Correo", type: "email" },
    ],
  },
  {
    section: "Profesional",
    fields: [
      { name: "estudios", label: "Estudios", type: "select", optionsFrom: "estudios" },
      { name: "oficio", label: "Oficio", type: "select", optionsFrom: "oficio" },
      { name: "s_laboral", label: "Situación laboral", type: "select", optionsFrom: "s_laboral" },
      { name: "disponibilidad", label: "Disponibilidad", type: "select", optionsFrom: "disponibilidad" },
      { name: "territorio", label: "Territorio", type: "select", optionsFrom: "territorio" },
      { name: "l_conducir", label: "Licencia de conducir", type: "select", optionsFrom: "l_conducir" },
      { name: "m_propia", label: "Movilización propia", type: "select", optionsFrom: "m_propia" },
      { name: "b_trabajo", label: "Bolsa de trabajo", type: "select", optionsFrom: "b_trabajo" },
      { name: "ingresos", label: "Ingresos", type: "select", optionsFrom: "ingresos" },
    ],
  },
  {
    section: "Personal",
    fields: [
      { name: "nacimiento", label: "Fecha de nacimiento", type: "date" },
      { name: "nacionalidad", label: "Nacionalidad", type: "select", optionsFrom: "nacionalidad" },
      { name: "e_civil", label: "Estado civil", type: "select", optionsFrom: "e_civil" },
      { name: "comuna", label: "Comuna", type: "select", optionsFrom: "comuna" },
      { name: "direccion", label: "Dirección", type: "text" },
      { name: "c_unica", label: "Clave Única", type: "select", optionsFrom: "c_unica" },
      { name: "sec", label: "Certificación SEC", type: "checkbox" },
    ],
  },
  {
    section: "Comentarios Entrevista",
    fields: [
      { name: "comentario_entrevista", label: "Déjanos tu impresión general de la Maestra y cualquier información nueva o relevante entregada por ella. Este texto se guardará como comentario del perfil.", type: "textarea" },
    ],
  },
];

const normalizeFormValue = (value) => {
  if (value === null || value === undefined) {
    return "";
  }

  return value;
};

const editableFieldNames = editableFields
  .flatMap((section) => section.fields)
  .map((field) => field.name);

const CommentAuthor = ({ author }) => {
  if (!author) return null;

  return (
    <img
      className="recopilador-comment-author"
      src={author.image}
      alt={author.name || "Usuario"}
      title={author.name || author.username || "Usuario"}
    />
  );
};

const RecopiladorPage = () => {
  const [maestras, setMaestras] = useState([]);
  const [selectedMaestra, setSelectedMaestra] = useState(null);
  const [formData, setFormData] = useState({});
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [missingFieldFilter, setMissingFieldFilter] = useState("");
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [modalErrorMessage, setModalErrorMessage] = useState("");
  const [fieldChoices, setFieldChoices] = useState({});

  const fetchCSRFToken = useCallback(async () => {
    const response = await fetch(
      `${process.env.REACT_APP_BASE_API}api/csrf_token/`,
      {
        method: "GET",
        credentials: "include",
        headers: {
          Accept: "application/json",
        },
      },
    );

    if (!response.ok) {
      throw new Error("Could not fetch CSRF token.");
    }

    const data = await response.json();

    return data.csrfToken;
  }, []);

  const fetchFieldChoices = useCallback(async () => {
    try {
      const response = await fetch(
        `${process.env.REACT_APP_BASE_API}api/perfiles/recopilador/choices/`,
        {
          method: "GET",
          credentials: "include",
          headers: {
            Accept: "application/json",
          },
        },
      );

      if (!response.ok) {
        throw new Error("Could not load field choices.");
      }

      const data = await response.json();
      setFieldChoices(data);
    } catch (error) {
      console.error("Error loading field choices:", error);
      setErrorMessage("No se pudieron cargar las listas del formulario.");
    }
  }, []);

  const fetchMaestras = useCallback(async () => {
    setIsLoading(true);
    setErrorMessage("");

    try {
      const response = await fetch(
        `${process.env.REACT_APP_BASE_API}api/perfiles/recopilador/`,
        {
          method: "GET",
          credentials: "include",
          headers: {
            Accept: "application/json",
          },
        },
      );

      if (!response.ok) {
        throw new Error("Could not load maestras.");
      }

      const data = await response.json();
      setMaestras(data);
    } catch (error) {
      console.error("Error loading recopilador table:", error);
      setErrorMessage("No se pudo cargar la información.");
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchMaestras();
    fetchFieldChoices();
  }, [fetchMaestras, fetchFieldChoices]);

  const filteredMaestras = useMemo(() => {
    return maestras.filter((maestra) => {
      const searchText = `${maestra.nombre || ""} ${maestra.rut || ""} ${maestra.comuna || ""} ${maestra.oficio || ""}`.toLowerCase();

      const matchesSearch = searchText.includes(searchTerm.toLowerCase());

      const matchesStatus =
        statusFilter === "all" ||
        (statusFilter === "complete" && maestra.completion_percentage === 100) ||
        (statusFilter === "pending" && maestra.completion_percentage < 100);

      const matchesMissingField =
        !missingFieldFilter ||
        (maestra.missing_fields || []).includes(missingFieldFilter);

      return matchesSearch && matchesStatus && matchesMissingField;
    });
  }, [maestras, missingFieldFilter, searchTerm, statusFilter]);

  const missingFieldOptions = useMemo(() => {
    const fields = new Set();

    maestras.forEach((maestra) => {
      (maestra.missing_fields || []).forEach((field) => fields.add(field));
    });

    return Array.from(fields).sort((left, right) =>
      left.localeCompare(right),
    );
  }, [maestras]);

  const openModal = (maestra) => {
    setSelectedMaestra(maestra);
    setFormData({ ...maestra, comentario_entrevista: "" });
    setModalErrorMessage("");
  };

  const syncSelectedMaestra = (updatedMaestras, rut) => {
    const updated = updatedMaestras.find((maestra) => maestra.rut === rut);

    if (updated) {
      setSelectedMaestra(updated);
      setFormData({ ...updated, comentario_entrevista: "" });
    }
  };

  const closeModal = () => {
    setSelectedMaestra(null);
    setFormData({});
    setIsSaving(false);
    setModalErrorMessage("");
  };

  const updateField = (fieldName, value) => {
    setFormData((currentData) => ({
      ...currentData,
      [fieldName]: value,
    }));
  };

  const formatApiErrors = (errors) => {
    if (!errors || typeof errors !== "object") {
      return "No se pudieron guardar los cambios.";
    }

    return Object.entries(errors)
      .map(([field, messages]) => {
        const fieldLabel =
          editableFields
            .flatMap((section) => section.fields)
            .find((item) => item.name === field)?.label || field;

        if (Array.isArray(messages)) {
          return `${fieldLabel}: ${messages.join(" ")}`;
        }

        return `${fieldLabel}: ${messages}`;
      })
      .join(" | ");
  };

  const saveMaestra = async () => {
    if (!selectedMaestra?.rut) {
      return;
    }

    setIsSaving(true);
    setModalErrorMessage("");

    try {
      const csrfToken = await fetchCSRFToken();
      
      const payload = editableFieldNames.reduce((currentPayload, fieldName) => {
        currentPayload[fieldName] = formData[fieldName];
        return currentPayload;
      }, {});
      delete payload.tipo;
      const comentarioEntrevista = (payload.comentario_entrevista || "").trim();
      delete payload.comentario_entrevista;
      delete payload.bios;

      const response = await fetch(
        `${process.env.REACT_APP_BASE_API}api/perfiles/maestras/${selectedMaestra.rut}/`,
        {
          method: "PATCH",
          credentials: "include",
          headers: {
            "Content-Type": "application/json",
            Accept: "application/json",
            "X-CSRFToken": csrfToken,
          },
          body: JSON.stringify(payload),
        },
      );

      const data = await response.json();

      if (!response.ok) {
        console.error("Recopilador save error:", data);

        setModalErrorMessage(
          data?.detail ||
            data?.error ||
            formatApiErrors(data),
        );

        return;
      }

      let newComment = null;

      if (comentarioEntrevista) {
        const commentResponse = await fetch(
          `${process.env.REACT_APP_BASE_API}api/perfiles/comentarios/`,
          {
            method: "POST",
            credentials: "include",
            headers: {
              "Content-Type": "application/json",
              Accept: "application/json",
              "X-CSRFToken": csrfToken,
            },
            body: JSON.stringify({
              maestra: selectedMaestra.rut,
              comment_text: comentarioEntrevista,
            }),
          },
        );

        if (!commentResponse.ok) {
          const commentData = await commentResponse.json().catch(() => ({}));
          setModalErrorMessage(
            commentData?.detail ||
              commentData?.error ||
              "Los datos se guardaron, pero no se pudo guardar el comentario.",
          );

          return;
        }

        newComment = await commentResponse.json();
      }

      const updatedResponse = await fetch(
        `${process.env.REACT_APP_BASE_API}api/perfiles/recopilador/`,
        {
          method: "GET",
          credentials: "include",
          headers: {
            Accept: "application/json",
          },
        },
      );

      if (!updatedResponse.ok) {
        throw new Error("Could not refresh maestras.");
      }

      const updatedMaestras = await updatedResponse.json();
      setMaestras(updatedMaestras);
      syncSelectedMaestra(updatedMaestras, selectedMaestra.rut);

      if (!newComment) {
        closeModal();
      }
    } catch (error) {
      console.error("Error saving maestra:", error);
      setModalErrorMessage("Error de conexión al guardar los cambios.");
    } finally {
      setIsSaving(false);
    }
  };

  const renderCompletionBadge = (percentage) => {
    if (percentage === 100) {
      return <span className="recopilador-badge complete">Completo</span>;
    }

    if (percentage >= 70) {
      return <span className="recopilador-badge medium">Avanzado</span>;
    }

    return <span className="recopilador-badge pending">Pendiente</span>;
  };

  return (
    <div className="recopilador-page">
      <div className="recopilador-header">
        <div>
          <h2>Recopilador</h2>
          <p>
            Revisión y actualización de datos de Maestras y Maestras Externas.
          </p>
        </div>
      </div>

      {errorMessage && (
        <Alert variant="danger" className="recopilador-alert">
          {errorMessage}
        </Alert>
      )}

      <div className="recopilador-filters">
        <Form.Control
          type="text"
          placeholder="Buscar por nombre, RUT, comuna u oficio..."
          value={searchTerm}
          onChange={(event) => setSearchTerm(event.target.value)}
        />

        <Form.Select
          value={statusFilter}
          onChange={(event) => setStatusFilter(event.target.value)}
        >
          <option value="all">Todos los estados</option>
          <option value="pending">Con información pendiente</option>
          <option value="complete">Completos</option>
        </Form.Select>

        <Form.Select
          value={missingFieldFilter}
          onChange={(event) => setMissingFieldFilter(event.target.value)}
        >
          <option value="">Todos los campos pendientes</option>
          {missingFieldOptions.map((field) => (
            <option key={field} value={field}>
              {field}
            </option>
          ))}
        </Form.Select>
      </div>

      <div className="recopilador-table-card">
        {isLoading ? (
          <div className="recopilador-loading">
            <Spinner animation="border" size="sm" />
            <span>Cargando información...</span>
          </div>
        ) : (
          <table className="recopilador-table">
            <thead>
              <tr>
                <th>Nombre</th>
                <th>Tipo</th>
                <th>Comuna</th>
                <th>Oficio</th>
                <th>Completitud</th>
                <th>Campos pendientes</th>
              </tr>
            </thead>

            <tbody>
              {filteredMaestras.map((maestra) => (
                <tr
                  key={maestra.rut}
                  onClick={() => openModal(maestra)}
                  className="recopilador-table-row"
                >
                  <td>
                    <strong>{maestra.nombre || "Sin nombre"}</strong>
                    <span>{maestra.rut}</span>
                  </td>
                  <td>{maestra.tipo || "Sin tipo"}</td>
                  <td>{maestra.comuna || "Sin comuna"}</td>
                  <td>{maestra.oficio || "Sin oficio"}</td>
                  <td>
                    <div className="recopilador-progress-cell">
                      <ProgressBar
                        now={maestra.completion_percentage}
                        label={`${maestra.completion_percentage}%`}
                      />
                      {renderCompletionBadge(maestra.completion_percentage)}
                    </div>
                  </td>
                  <td>
                    {maestra.missing_fields?.length > 0 ? (
                      <span>{maestra.missing_fields.slice(0, 3).join(", ")}</span>
                    ) : (
                      <span className="recopilador-muted">Sin pendientes</span>
                    )}
                  </td>
                </tr>
              ))}

              {filteredMaestras.length === 0 && (
                <tr>
                  <td colSpan="6" className="recopilador-empty">
                    No hay registros para mostrar.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        )}
      </div>

      <Modal
        show={Boolean(selectedMaestra)}
        onHide={closeModal}
        size="lg"
        centered
      >
        <Modal.Header closeButton>
          <Modal.Title>
            {selectedMaestra?.nombre || "Actualizar información"}
          </Modal.Title>
        </Modal.Header>

        <Modal.Body>
          {selectedMaestra && (
            <>
              {modalErrorMessage && (
                <Alert variant="danger" className="recopilador-modal-alert">
                  {modalErrorMessage}
                </Alert>
              )}
              <div className="recopilador-modal-summary">
                <div>
                  <strong>RUT:</strong> {selectedMaestra.rut}
                </div>
                <div>
                  <strong>Completitud:</strong>{" "}
                  {selectedMaestra.completion_percentage}%
                </div>
              </div>

              {selectedMaestra.missing_fields?.length > 0 && (
                <div className="recopilador-missing-box">
                  <strong>Campos pendientes:</strong>
                  <p>{selectedMaestra.missing_fields.join(", ")}</p>
                </div>
              )}

              <div className="recopilador-history-box">
                <strong>Programas aprobados:</strong>

                {selectedMaestra.cursos_aprobados?.length > 0 ||
                selectedMaestra.actividades_aprobadas?.length > 0 ? (
                  <ul>
                    {(selectedMaestra.cursos_aprobados || []).map((curso) => (
                      <li key={`curso-${curso.id}`}>
                        <strong>Curso:</strong> {curso.nombre || "Curso sin nombre"}
                        {curso.comuna ? ` - ${curso.comuna}` : ""}
                        {curso.f_inicio ? ` (${curso.f_inicio})` : ""}
                      </li>
                    ))}

                    {(selectedMaestra.actividades_aprobadas || []).map((actividad) => (
                      <li key={`actividad-${actividad.id}`}>
                        <strong>Actividad:</strong>{" "}
                        {actividad.nombre || "Actividad sin nombre"}
                        {actividad.comuna ? ` - ${actividad.comuna}` : ""}
                        {actividad.f_inicio ? ` (${actividad.f_inicio})` : ""}
                      </li>
                    ))}
                  </ul>
                ) : (
                  <p>No registra cursos ni actividades aprobadas.</p>
                )}
              </div>

              {editableFields.map((section) => (
                <div key={section.section} className="recopilador-form-section">
                  <h5>{section.section}</h5>

                  <div className="recopilador-form-grid">
                    {section.fields.map((field) => {
                      const value = normalizeFormValue(formData[field.name]);

                      if (field.type === "textarea") {
                        return (
                          <Form.Group key={field.name} className="recopilador-form-field wide">
                            <Form.Label>{field.label}</Form.Label>
                            <Form.Control
                              as="textarea"
                              rows={4}
                              value={value}
                              onChange={(event) =>
                                updateField(field.name, event.target.value)
                              }
                            />
                            {field.name === "comentario_entrevista" && (
                              <div className="recopilador-comments-history">
                                <div className="recopilador-comments-history-header">
                                  <strong>Comentarios existentes</strong>
                                  <span>
                                    {selectedMaestra.comentarios?.length || 0}
                                  </span>
                                </div>

                                {selectedMaestra.comentarios?.length > 0 ? (
                                  selectedMaestra.comentarios.map((comment) => (
                                    <div
                                      key={comment.id}
                                      className="recopilador-comment-item"
                                    >
                                      <p>{comment.comment_text}</p>
                                      <div className="recopilador-comment-meta">
                                        <CommentAuthor
                                          author={comment.created_by_user}
                                        />
                                        <span>
                                          ✔️✔️ {comment.date_observed}
                                        </span>
                                      </div>
                                    </div>
                                  ))
                                ) : (
                                  <p className="recopilador-comments-empty">
                                    Aún no hay comentarios guardados.
                                  </p>
                                )}
                              </div>
                            )}
                          </Form.Group>
                        );
                      }

                      if (field.type === "select") {
                        const options = fieldChoices[field.optionsFrom] || [];

                        return (
                          <Form.Group key={field.name} className="recopilador-form-field">
                            <Form.Label>{field.label}</Form.Label>
                            <Form.Select
                              value={value}
                              disabled={field.disabled}
                              onChange={(event) =>
                                updateField(field.name, event.target.value)
                              }
                            >
                              <option value="">---------</option>

                              {options.map((option) => (
                                <option key={option.value} value={option.value}>
                                  {option.label}
                                </option>
                              ))}
                            </Form.Select>
                          </Form.Group>
                        );
                      }

                      if (field.type === "checkbox") {
                        return (
                          <Form.Group key={field.name} className="recopilador-form-field checkbox">
                            <Form.Check
                              type="checkbox"
                              label={field.label}
                              checked={Boolean(value)}
                              onChange={(event) =>
                                updateField(field.name, event.target.checked)
                              }
                            />
                          </Form.Group>
                        );
                      }

                      return (
                        <Form.Group key={field.name} className="recopilador-form-field">
                          <Form.Label>{field.label}</Form.Label>
                          <Form.Control
                            type={field.type}
                            value={value}
                            onChange={(event) =>
                              updateField(field.name, event.target.value)
                            }
                          />
                        </Form.Group>
                      );
                    })}
                  </div>
                </div>
              ))}
            </>
          )}
        </Modal.Body>

        <Modal.Footer>
          <Button variant="secondary" onClick={closeModal} disabled={isSaving}>
            Cancelar
          </Button>
          <Button variant="success" onClick={saveMaestra} disabled={isSaving}>
            {isSaving ? "Guardando..." : "Guardar cambios"}
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default RecopiladorPage;
